/**
 * Shared blocks (K4 part 2, Editor 2.0).
 *
 * A shared block is a piece of mail HTML saved once (`shared_blocks`, tenant
 * migration 0077) and referenced from many templates with the plain-text
 * directive
 *
 *   {{np_block <id>}}
 *
 * The directive is stored in the template and in a draft campaign as
 * written; it survives the save-time sanitiser like `{{#np_show}}` does.
 * It is replaced by the block's current HTML in ONE place per mail version:
 * when a campaign is prepared (frozen into the snapshot), when a flow step
 * sends (the snapshot is rewritten per send), and in the previews. So an
 * edit reaches every draft or flow mail the next time it is rendered, while
 * an approved campaign keeps the exact HTML that was approved.
 *
 * Reads are bounded: one query for ALL blocks a mail uses, and nothing at
 * all for a mail without a directive. The queue consumer never expands
 * (the snapshot is already expanded), so a send batch adds no reads.
 *
 * This file is pure (no database), so the admin app can use it for its live
 * preview.
 */

/** Distinct shared blocks one mail may reference (bounds the single IN query). */
export const MAX_SHARED_BLOCKS_PER_MAIL = 20;
/** Size cap of one shared block's HTML. */
export const MAX_SHARED_BLOCK_HTML_BYTES = 40 * 1024;

const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const DIRECTIVE = /\{\{np_block ([A-Za-z0-9_-]{1,64})\}\}/g;

export function isSharedBlockId(id: string): boolean {
  return ID_PATTERN.test(id);
}

/** The directive text for a block id. */
export function sharedBlockDirective(id: string): string {
  return `{{np_block ${id}}}`;
}

/** True when `source` mentions a shared block directive at all (a cheap check before any work). */
export function hasSharedBlocks(source: string): boolean {
  return source.includes('{{np_block');
}

/** Distinct block ids referenced, in first-seen order. */
export function sharedBlockIdsOf(source: string): string[] {
  if (!hasSharedBlocks(source)) return [];
  const ids: string[] = [];
  for (const m of source.matchAll(DIRECTIVE)) if (!ids.includes(m[1]!)) ids.push(m[1]!);
  return ids;
}

/** Any `{{np_block` that is not a well-formed directive (wrong id characters, missing space, ...). */
export function malformedSharedBlockRefs(source: string): number {
  if (!hasSharedBlocks(source)) return 0;
  const all = source.split('{{np_block').length - 1;
  const good = [...source.matchAll(DIRECTIVE)].length;
  return all - good;
}

export interface SharedBlockExpansion {
  readonly html: string;
  /** Ids referenced but not found: replaced by nothing. */
  readonly missing: readonly string[];
  /** Ids that were found and put in. */
  readonly used: readonly string[];
}

/** Removes every directive (and any broken `{{np_block ...}}` text). The same string back without one. */
export function stripSharedBlockRefs(source: string): string {
  return hasSharedBlocks(source) ? source.replace(/\{\{np_block[^{}]*\}\}/g, '') : source;
}

/**
 * Replaces every directive with the block's HTML. The same string comes back
 * when there is no directive. Not recursive: a block's own directives are
 * removed (a block cannot contain another block; saving one is refused), so
 * no `{{np_block}}` text can reach the mail renderer.
 */
export function expandSharedBlocks(
  source: string,
  blocks: ReadonlyMap<string, string>,
): SharedBlockExpansion {
  if (!hasSharedBlocks(source)) return { html: source, missing: [], used: [] };
  const missing: string[] = [];
  const used: string[] = [];
  const replaced = source.replace(DIRECTIVE, (_whole, id: string) => {
    const body = blocks.get(id);
    if (body === undefined) {
      if (!missing.includes(id)) missing.push(id);
      return '';
    }
    if (!used.includes(id)) used.push(id);
    return stripSharedBlockRefs(body);
  });
  // Whatever is left of the marker is broken text: never let it reach the renderer.
  return { html: stripSharedBlockRefs(replaced), missing, used };
}

export interface SharedBlockHtmlError {
  readonly code: 'empty' | 'too_large' | 'nested';
  readonly message: string;
}

/** Save-time check of a shared block's own HTML. */
export function checkSharedBlockHtml(html: string): SharedBlockHtmlError[] {
  const errors: SharedBlockHtmlError[] = [];
  if (html.trim() === '') errors.push({ code: 'empty', message: 'The block is empty.' });
  if (new TextEncoder().encode(html).length > MAX_SHARED_BLOCK_HTML_BYTES) {
    errors.push({
      code: 'too_large',
      message: `A shared block can be at most ${MAX_SHARED_BLOCK_HTML_BYTES / 1024} KB.`,
    });
  }
  if (hasSharedBlocks(html)) {
    errors.push({ code: 'nested', message: 'A shared block cannot contain another shared block.' });
  }
  return errors;
}

/** Errors for a mail that uses shared blocks wrongly (too many, or a broken directive). */
export function checkSharedBlockRefs(source: string): string[] {
  const errors: string[] = [];
  if (malformedSharedBlockRefs(source) > 0) {
    errors.push('A shared block reference is broken. Pick the block again in the editor.');
  }
  if (sharedBlockIdsOf(source).length > MAX_SHARED_BLOCKS_PER_MAIL) {
    errors.push(`A mail can use at most ${MAX_SHARED_BLOCKS_PER_MAIL} different shared blocks.`);
  }
  return errors;
}
